import * as React from "react";
import { useCallback } from "react";
import { Button, styled, Theme } from "@mui/material";
import clsx from "clsx";
import { DocQuestion } from "../../DocQuestion";
import { QuestionsIF } from "../../QuestionsIF";
import { QualifierValue } from "fe-common/graphql";
import { qualifierValueToQualifierFragment } from "../../../../utils/dataHelpers";
import { ReactComponent as SeverityIcons } from "../../../../assets/severityIcons.svg";
import {
  RefinementRangeSortType,
  useRangeRefinements,
} from "./hooks/useRangeRefinements";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
//region [[ Styles ]]

const SeverityQuestionView = styled("div")({
  width: "100%",
});

const QuestionContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  width: "100%",
});

const ColorsRangeContainer = styled("div")({
  position: "relative",
  display: "flex",
  flexFlow: "column",
  border: "1px solid #F7F7F7",
  borderRadius: "14px",
  margin: "19px 0",
});

const SvgIconContainer = styled("div")({
  position: "absolute",
  right: "5%",
  top: "5%",
  height: "90%",
  zIndex: 1500,
  overflow: "hidden",
  // Flip to left in hebrew/arabic
  ...onRtl({
    right: "auto",
    left: "5%",
  }),
  "& svg": {
    height: "100%",
  },
});
// We want to go from green to red which it 2/3 of a circle in the color
// wheel (https://krazydad.com/tutorials/exampleHuePath.png) normalized by the
// number of items
const frequency = (itemsNumber) => (0.666 * Math.PI) / itemsNumber;
const amplitude = 35;
const center = 200;

const ColorButton = styled(Button)(
  ({
    theme,
    index,
    itemsNumber,
  }: {
    theme?: Theme;
    index: number;
    itemsNumber: number;
  }) => ({
    "&.color-button": {
      borderRadius: "0",
      padding: "10px 25px",
      // Make background spin through the color wheel while
      // starting at green and ending at red (fequency -> how fast will we go from
      // one tolcer to another)
      // see: https://krazydad.com/tutorials/makecolors.php for understanding how it works
      background: `rgba(${Math.sin(frequency(itemsNumber) * index + 0) *
        amplitude +
        center},
      ${Math.sin(frequency(itemsNumber) * index + 8) * amplitude + center},
      ${Math.sin(frequency(itemsNumber) * index + 4) * amplitude +
        center},1) !important`,
      "&:hover,&:active,&:focus": {
        padding: "10px 25px",
        background: `rgba(${Math.sin(frequency(itemsNumber) * index + 0) *
          amplitude +
          center},
      ${Math.sin(frequency(itemsNumber) * index + 8) * amplitude + center},
      ${Math.sin(frequency(itemsNumber) * index + 4) * amplitude +
        center},0.8) important`,
      },
      // Adjust inner text
      textTransform: "none",
      display: "flex",
      flexFlow: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      "&:first-of-type": {
        borderTopLeftRadius: "14px ",
        borderTopRightRadius: "14px",
      },
      "&:last-of-type": {
        borderBottomLeftRadius: "14px",
        borderBottomRightRadius: "14px",
      },
    },
  }),
);

// const Index = styled("span")({
//   margin: "0 5px",
// });

const QualifierText = styled("span")({
  margin: "0 20px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SeverityQuestionProps extends QuestionsIF {
  className?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const SeverityQuestion = ({
  answer,
  question,
  onAnswer,
  ...props
}: SeverityQuestionProps) => {
  const suggestion = question?.suggestions?.[0];

  const { rangeItems, relation, abstractSuggestion } = useRangeRefinements({
    suggestion,
    sortType: RefinementRangeSortType.Descending,
  });

  const isAnswered = !!answer;

  const handleRefinementSelected = useCallback(
    (q: QualifierValue) => () => {
      const negatedQualifiers = rangeItems
        .filter((r) => r.value.id !== q.id)
        .map((toNegate) =>
          qualifierValueToQualifierFragment(toNegate.value, relation),
        );

      const answerValue = [
        {
          ...abstractSuggestion,
          qualifiers: [qualifierValueToQualifierFragment(q, relation)],
        },
        { ...abstractSuggestion, qualifiers: negatedQualifiers, skip: true },
      ];

      onAnswer(answerValue);
    },
    [rangeItems, relation, abstractSuggestion, onAnswer],
  );

  return (
    <SeverityQuestionView
      className={clsx(props.className, "severity-question-view")}
    >
      <QuestionContainer className={"question-container"}>
        <DocQuestion
          text={suggestion.question.text}
          miniView={props.miniView}
        />
      </QuestionContainer>
      {!isAnswered && (
        <ColorsRangeContainer>
          <SvgIconContainer>
            <SeverityIcons />
          </SvgIconContainer>
          {rangeItems.map((re, index) => (
            <ColorButton
              className={"color-button"}
              key={index}
              index={index}
              itemsNumber={rangeItems.length}
              onClick={handleRefinementSelected(re.value)}
            >
              {/*<Index>{index}</Index>*/}
              <QualifierText>{re.value.shortname}</QualifierText>
            </ColorButton>
          ))}
        </ColorsRangeContainer>
      )}
    </SeverityQuestionView>
  );
};
